import { BaseInputDevice, Debouncer } from './InputDevice.ts';

/**
 * WebSocket adapter, for hardware that lives behind a small local bridge
 * process rather than on the browser's own serial port.
 *
 * This is usually the more practical option in a lab: a Python or Node bridge
 * owns the serial link, can timestamp edges closer to the metal, can talk to a
 * DAQ or an existing acquisition program, and does not care which browser the
 * researcher happens to be using.
 *
 * ## Message format (JSON, one object per frame)
 *
 *   { "type": "button", "down": true,  "t": 1234.5 }
 *   { "type": "emg",    "value": 812, "channel": 0, "t": 1234.5 }
 *   { "type": "hello",  "device": "teensy-emg-01" }
 *
 * `t` is optional. When present it must be a millisecond timestamp on the
 * *browser's* `performance.now()` timeline; a bridge that cannot guarantee that
 * should omit it, and arrival time is used instead.
 */
export interface WebSocketInputOptions {
  url: string;
  debounceMs: number;
  /** Reconnect automatically after an unexpected close. */
  autoReconnect: boolean;
  reconnectDelayMs: number;
}

export const DEFAULT_WEBSOCKET_OPTIONS: WebSocketInputOptions = {
  url: 'ws://localhost:8765',
  debounceMs: 25,
  autoReconnect: true,
  reconnectDelayMs: 1000,
};

export class WebSocketInput extends BaseInputDevice {
  readonly id = 'websocket-input';
  readonly label: string;
  override readonly providesEmg = true;

  readonly options: WebSocketInputOptions;

  private socket: WebSocket | null = null;
  private debouncer: Debouncer;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closing = false;
  private messageCount = 0;
  private badMessageCount = 0;

  constructor(options: Partial<WebSocketInputOptions> = {}) {
    super();
    this.options = { ...DEFAULT_WEBSOCKET_OPTIONS, ...options };
    this.label = `WebSocket bridge (${this.options.url})`;
    this.debouncer = new Debouncer(this.options.debounceMs);
  }

  async connect(): Promise<void> {
    this.closing = false;
    this.status = 'connecting';
    await new Promise<void>((resolve, reject) => {
      try {
        this.socket = new WebSocket(this.options.url);
      } catch (err) {
        this.status = 'error';
        this.lastError = String(err);
        reject(err);
        return;
      }
      this.socket.addEventListener('open', () => {
        this.status = 'connected';
        this.lastError = null;
        resolve();
      });
      this.socket.addEventListener('message', (e) => this.handleMessage(e));
      this.socket.addEventListener('error', () => {
        this.lastError = 'websocket error';
        if (this.status === 'connecting') {
          this.status = 'error';
          reject(new Error(`Could not connect to ${this.options.url}`));
        }
      });
      this.socket.addEventListener('close', () => {
        this.socket = null;
        if (this.closing) {
          this.status = 'disconnected';
          return;
        }
        this.status = 'error';
        if (this.options.autoReconnect) {
          this.reconnectTimer = setTimeout(
            () => void this.connect().catch(() => undefined),
            this.options.reconnectDelayMs,
          );
        }
      });
    });
  }

  async disconnect(): Promise<void> {
    this.closing = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.socket?.close();
    this.socket = null;
    this.debouncer.reset();
    this.status = 'disconnected';
  }

  override info(): Record<string, unknown> {
    return {
      ...super.info(),
      url: this.options.url,
      messages: this.messageCount,
      badMessages: this.badMessageCount,
    };
  }

  private handleMessage(e: MessageEvent): void {
    this.messageCount++;
    const arrival = performance.now();
    let msg: unknown;
    try {
      msg = JSON.parse(String(e.data));
    } catch {
      this.badMessageCount++;
      return;
    }
    if (typeof msg !== 'object' || msg === null) {
      this.badMessageCount++;
      return;
    }
    const m = msg as Record<string, unknown>;
    const timestamp = typeof m.t === 'number' ? m.t : arrival;

    if (m.type === 'button' && typeof m.down === 'boolean') {
      if (this.debouncer.accept(m.down, timestamp)) {
        this.emitButton({ down: m.down, timestamp, source: this.id });
      }
      return;
    }
    if (m.type === 'emg' && typeof m.value === 'number') {
      this.emitEmg({
        value: m.value,
        timestamp,
        channel: typeof m.channel === 'number' ? m.channel : 0,
        source: this.id,
      });
      return;
    }
    if (m.type === 'hello') return;
    this.badMessageCount++;
  }
}
